import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Select from '../../components/common/Select';
import Pagination from '../../components/common/Pagination';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  isRead: boolean;
  createdAt: string;
}

export default function NotificationList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const filter = (searchParams.get('filter') as 'all' | 'unread') || 'all';
  const setFilter = (value: 'all' | 'unread') => {
    setSearchParams(prev => { prev.set('filter', value); prev.set('page', '1'); return prev; }, { replace: true });
  };
  
  // ページネーション・表示件数（URL同期）
  const currentPage = parseInt(searchParams.get('page') || '1');
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const setCurrentPage = (page: number | ((prev: number) => number)) => {
    const p = typeof page === 'function' ? page(currentPage) : page;
    setSearchParams(prev => { prev.set('page', String(p)); return prev; }, { replace: true });
  };
  const setPageSize = (size: number) => {
    setSearchParams(prev => { prev.set('pageSize', String(size)); prev.set('page', '1'); return prev; }, { replace: true });
  };

  useEffect(() => {
    fetchNotifications();
  }, [filter, currentPage, pageSize]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter === 'unread') {
        params.append('unread', 'true');
      }
      params.append('page', String(currentPage));
      params.append('pageSize', String(pageSize));
      
      const data = await api.get<{ items: Notification[]; total: number; totalPages: number }>(`/api/notifications?${params.toString()}`);
      setNotifications(Array.isArray(data?.items) ? data.items : []);
      setTotalPages(data?.totalPages ?? 1);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await api.put(`/api/notifications/${id}/read`, {});
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
    } catch {
      // Silently fail
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.put('/api/notifications/read-all', {});
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch {
      // Silently fail
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/api/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch {
      // Silently fail
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'mention':
        return { icon: '💬', label: 'メンション', color: 'bg-blue-100 text-blue-600' };
      case 'task':
        return { icon: '📋', label: 'タスク', color: 'bg-green-100 text-green-600' };
      case 'request':
      case 'request_assigned':
        return { icon: '📩', label: '要請', color: 'bg-yellow-100 text-yellow-600' };
      case 'construction':
        return { icon: '🔨', label: '工事', color: 'bg-orange-100 text-orange-600' };
      case 'board':
        return { icon: '📢', label: '掲示板', color: 'bg-purple-100 text-purple-600' };
      case 'survey':
        return { icon: '📊', label: 'アンケート', color: 'bg-pink-100 text-pink-600' };
      case 'calendar':
        return { icon: '📅', label: 'カレンダー', color: 'bg-cyan-100 text-cyan-600' };
      case 'daily_report':
        return { icon: '📝', label: '日報', color: 'bg-teal-100 text-teal-600' };
      default:
        return { icon: '🔔', label: 'システム', color: 'bg-gray-100 text-gray-600' };
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'たった今';
    if (minutes < 60) return `${minutes}分前`;
    if (hours < 24) return `${hours}時間前`;
    if (days < 7) return `${days}日前`;
    return date.toLocaleDateString('ja-JP', { month: 'long', day: 'numeric' });
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">通知</h1>
          {unreadCount > 0 && (
            <p className="text-sm text-gray-500 mt-1">{unreadCount}件の未読通知があります</p>
          )}
        </div>
        {unreadCount > 0 && (
          <Button variant="secondary" size="sm" onClick={handleMarkAllAsRead}>
            すべて既読にする
          </Button>
        )}
      </div>

      {/* フィルタ */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex gap-2">
          <Button
            variant={filter === 'all' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            すべて
          </Button>
          <Button
            variant={filter === 'unread' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilter('unread')}
          >
            未読のみ
          </Button>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>表示件数:</span>
          <Select
            value={pageSize.toString()}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="w-24"
          >
            <option value="10">10</option>
            <option value="30">30</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="1000">1000</option>
          </Select>
        </div>
      </div>

      {/* 通知一覧 */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : notifications.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔔</div>
            <p className="text-gray-500">
              {filter === 'unread' ? '未読の通知はありません' : '通知はありません'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => {
            const typeInfo = getNotificationIcon(notification.type);
            return (
              <Card
                key={notification.id}
                className={`transition-all ${
                  !notification.isRead ? 'border-l-4 border-l-blue-500' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${typeInfo.color}`}>
                    <span className="text-xl">{typeInfo.icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="default" size="sm">{typeInfo.label}</Badge>
                      <span className="text-xs text-gray-400">{formatDate(notification.createdAt)}</span>
                      {!notification.isRead && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full" />
                      )}
                    </div>
                    <h3 className="font-medium text-gray-900 dark:text-white">{notification.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">{notification.message}</p>
                    <div className="flex items-center gap-3 mt-3">
                      {notification.link && (
                        <Link
                          to={notification.link}
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          詳細を見る →
                        </Link>
                      )}
                      {!notification.isRead && (
                        <button
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          既読にする
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(notification.id)}
                        className="text-sm text-red-500 hover:text-red-700"
                      >
                        削除
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* ページネーション */}
      <div className="flex justify-center">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}
